// FPCard - Card-style display for franchise partner in FP Directory
// Features: Full-width details, contact inline, icon footer bar

import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Tooltip
} from '@mui/material'
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon
} from '@mui/icons-material'

import { poolwerxColors } from '../../../theme/colors'
import { getCountryChipConfig } from '../../../utils/countryChips'
import { LightspeedIcon, XeroIcon, SkimmerIcon, InspectorIcon } from '../../../components/SystemIcons'
import { generateLightspeedMainUrl, generateXeroMainUrl, generateInspectorUrl, generateAdminFpUrl } from '../../../utils/urls'
import type { FranchisePartner } from '../types'
import { getStatusColor, getFranchiseeName } from '../utils'
import StatusChip from './StatusChip'
import { getResourceIcon, getResourceShortLabel } from '../../../utils/resourceIcons'

interface FPCardProps {
  fp: FranchisePartner
}

// Icon button with label component
interface LabeledIconButtonProps {
  icon: React.ReactNode
  label: string
  href?: string
  to?: string
  disabled?: boolean
  tooltip?: string
  onClick?: () => void
}

const LabeledIconButton: React.FC<LabeledIconButtonProps> = ({
  icon,
  label,
  href,
  to,
  disabled = false,
  tooltip,
  onClick
}) => {
  const buttonContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
        opacity: disabled ? 0.3 : 1,
        transition: 'all 0.2s ease',
        cursor: disabled ? 'default' : 'pointer',
        p: 0.5,
        borderRadius: 1,
        '&:hover': disabled ? {} : {
          backgroundColor: 'rgba(0, 127, 152, 0.08)'
        }
      }}
    >
      <Box sx={{ fontSize: '1.4rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.52rem',
          fontWeight: 500,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.3px'
        }}
      >
        {label}
      </Typography>
    </Box>
  )

  const wrappedButton = to ? (
    <Box
      component={RouterLink}
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ textDecoration: 'none', color: 'inherit' }}
    >
      {buttonContent}
    </Box>
  ) : href ? (
    <Box
      component="a"
      href={disabled ? undefined : href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ textDecoration: 'none', color: 'inherit' }}
      onClick={(e: React.MouseEvent) => disabled && e.preventDefault()}
    >
      {buttonContent}
    </Box>
  ) : (
    <Box onClick={disabled ? undefined : onClick}>
      {buttonContent}
    </Box>
  )

  return tooltip ? (
    <Tooltip title={tooltip}>
      <span>{wrappedButton}</span>
    </Tooltip>
  ) : wrappedButton
}

const FPCard: React.FC<FPCardProps> = ({ fp }) => {
  const countryConfig = getCountryChipConfig(fp.country)
  const franchiseeName = getFranchiseeName(fp)
  const isTerminated = fp.fp_status === 'Terminated'

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid #E0E0E0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'all 0.2s ease',
        opacity: isTerminated ? 0.6 : 1,
        filter: isTerminated ? 'grayscale(40%)' : 'none',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header Bar */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 127, 152, 0.04)',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0'
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                color: poolwerxColors.teal,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {fp.franchise_name}
            </Typography>
            <Chip
              label={countryConfig.emoji}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: countryConfig.backgroundColor,
                borderColor: countryConfig.borderColor,
                fontWeight: 500,
                flexShrink: 0,
                minWidth: 'auto',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Stack>
          <Box sx={{ flexShrink: 0, ml: 1 }}>
            <StatusChip status={fp.fp_status} getColor={getStatusColor} size="small" />
          </Box>
        </Box>

        {/* Content Area - Full Width Details */}
        <Box sx={{ p: 2.5 }}>
          {/* FP Details Row - Horizontal Layout */}
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
            <Chip
              label={fp.market || '-'}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LocationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {fp.region_name || 'No region'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {fp.bdm_name ? `${fp.bdm_name} (ROM/FBC)` : 'No ROM/FBC'}
              </Typography>
            </Stack>
          </Stack>

          {/* Contact Section - Full Width */}
          <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}
            >
              Contact
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {franchiseeName !== '-' ? franchiseeName : 'No contact name'}
            </Typography>
            {/* Email and Phone Inline */}
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              {fp.franchisee_email && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <EmailIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                  <Typography
                    component="a"
                    href={`mailto:${fp.franchisee_email}`}
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: poolwerxColors.teal,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {fp.franchisee_email}
                  </Typography>
                </Stack>
              )}
              {fp.franchisee_mobile && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PhoneIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                  <Typography
                    component="a"
                    href={`tel:${fp.franchisee_mobile}`}
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: poolwerxColors.teal,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {fp.franchisee_mobile}
                  </Typography>
                </Stack>
              )}
              {!fp.franchisee_email && !fp.franchisee_mobile && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  No contact details
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Footer Bar - Icons (20% smaller) */}
        <Box
          sx={{
            borderTop: '1px solid #f0f0f0',
            backgroundColor: 'rgba(0, 127, 152, 0.02)',
            px: 2.5,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          {/* EVO Platform - Left */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.6rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}
            >
              EVO Platform
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <LabeledIconButton
                icon={<LightspeedIcon size={22} />}
                label="Lightspeed"
                href={fp.lightspeed_store_code ? generateLightspeedMainUrl(fp.lightspeed_store_code) : undefined}
                disabled={!fp.lightspeed_store_code}
                tooltip={fp.lightspeed_store_code ? 'Open Lightspeed' : 'No Lightspeed connection'}
              />
              <LabeledIconButton
                icon={<XeroIcon size={22} />}
                label="Xero"
                href={fp.xero_short_code ? generateXeroMainUrl(fp.xero_short_code) : undefined}
                disabled={!fp.xero_short_code}
                tooltip={fp.xero_short_code ? 'Open Xero' : 'No Xero connection'}
              />
              {fp.country === 'US' && (
                <LabeledIconButton
                  icon={<SkimmerIcon size={22} />}
                  label="Skimmer"
                  href={fp.has_complete_skimmer_connection ? 'https://app.getskimmer.com' : undefined}
                  disabled={!fp.has_complete_skimmer_connection}
                  tooltip={fp.has_complete_skimmer_connection ? 'Open Skimmer' : 'No Skimmer connection'}
                />
              )}
              <LabeledIconButton
                icon={<InspectorIcon size={22} />}
                label="Inspector"
                to={generateInspectorUrl(fp.fp_code, { id: fp.id })}
                tooltip="Open Inspector"
              />
            </Stack>
          </Box>

          {/* Quick Links - Right */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.6rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}
            >
              Quick Links
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {(fp.resources ?? []).map(resource => {
                const Icon = getResourceIcon(resource.resource_type)
                const url = resource.url
                return (
                  <LabeledIconButton
                    key={resource.resource_code}
                    icon={<Icon size={22} />}
                    label={getResourceShortLabel(resource.resource_type)}
                    href={url || undefined}
                    disabled={!url}
                    tooltip={url ? resource.label : `${resource.label} (not configured)`}
                  />
                )
              })}
              <LabeledIconButton
                icon={<Box sx={{ display: 'flex', alignItems: 'center' }}><EditIcon sx={{ fontSize: 19 }} /><span style={{ fontSize: '0.75rem', marginLeft: -3 }}>↗</span></Box>}
                label="Edit"
                to={generateAdminFpUrl(fp.id, 'edit')}
                tooltip="Edit in Admin"
              />
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default FPCard
